import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";

const TEAM_FORMATS = [
  "Team Medal", "Team Stableford", "Team Match Play", "Team Aggregate Stableford", "Team Aggregate Stroke Play", "Texas Scramble", "Texas Scramble (4 players)", "Texas Scramble (3 players)", "Texas Scramble Medal (4 players)", "Texas Scramble Medal (3 players)",
  "Better Ball Stableford", "Better Ball Medal", "Better Ball Stroke Play", "Better Ball Match Play",
  "Fourball Better Ball Stableford", "Fourball Match Play", "Fourball Stroke Play",
  "Foursomes Stableford", "Foursomes Match Play", "Foursomes Medal",
  "Greensomes Stableford", "Greensomes Medal", "Greensomes Match Play",
  "Team Stableford (Best 2 of 4)"
];
// Four players, no pairing (a Groups.jsx grp, not a Pairs team_id) — best 2
// individual net/Stableford scores per hole count toward the team total.
// Full handicap, no 85%-of-pair reduction (that reduction is specific to
// the 2-person Better Ball formats above).
const GROUP_BEST_2_OF_4_FORMATS = ["Team Stableford (Best 2 of 4)"];
// Singles Match Play was removed from this list 2026-09-04 — Mike wants no
// opponent-pairing concept anywhere for this format ("they are not pairs or
// teams the clue is in the word SINGLE"), so it now falls through to the
// same flat individual leaderboard as Singles Stableford/Medal instead of
// the head-to-head match branch below.
const MATCH_PLAY_FORMATS = [
  "Better Ball Match Play", "Fourball Match Play",
  "Foursomes Match Play", "Greensomes Match Play", "Team Match Play"
];
const TEAM_MATCH_PLAY_FORMATS = ["Better Ball Match Play", "Fourball Match Play", "Foursomes Match Play", "Greensomes Match Play", "Team Match Play"];
// Stroke-play alternate-shot formats only — Match Play versions already go
// through the separate matchPlayMode branch above, which is unaffected by
// this fix (already filters phantom-zero rows correctly via `&& s.gross`).
const ALTERNATE_SHOT_FORMATS = ["Foursomes Stableford", "Foursomes Medal", "Greensomes Stableford", "Greensomes Medal"];
const BETTER_BALL_HCP_FORMATS = ["Better Ball Stableford", "Better Ball Medal", "Better Ball Stroke Play", "Fourball Better Ball Stableford", "Fourball Stroke Play"];
// Mike's authoritative HCP allowance table (confirmed 2026-09-04) — see the
// matching constant in IndividualScoreEntry.jsx for the full explanation.
// Note: this does NOT cover Foursomes/Greensomes, which need their own
// 50%-combined / 60-40-split pair formulas — not yet built into this file's
// team-leaderboard branch (pre-existing gap, found 2026-09-04, flagged
// separately rather than folded into this fix).
const HCP_95_FORMATS = ["Singles Stableford", "Singles Medal", "Singles Stroke Play", "Team Stableford", "Team Medal", "Team Aggregate Stableford", "Team Aggregate Stroke Play"];

// Match play never uses a side's/player's full allowance directly — the
// LOWEST allowance anywhere in the match plays off scratch, everyone else
// gets the difference, allocated by stroke index. Must match
// MatchPlayScoreEntry.jsx / MatchResults.jsx exactly.
function getMatchType(gameType) {
  // "Better Ball Match Play" is the same fourball mechanic as "Fourball
  // Match Play" (each partner plays their own ball, better one counts)
  // despite the different name — missing here until 2026-09-06, it was
  // silently scored as a singles 1v1 match instead.
  if (gameType === "Fourball Match Play" || gameType === "Better Ball Match Play") return "fourball";
  if (gameType === "Foursomes Match Play" || gameType === "Greensomes Match Play") return "alternate";
  return "singles";
}
function mpSideAllowance(side, matchType, gameType) {
  if (matchType === "alternate") {
    const combined = side.reduce((s, p) => s + ((p.hcp_adj ?? p.handicap) || 0), 0);
    if (gameType === "Greensomes Match Play") {
      const lower = Math.min(...side.map(p => (p.hcp_adj ?? p.handicap) || 0));
      const higher = Math.max(...side.map(p => (p.hcp_adj ?? p.handicap) || 0));
      return Math.round(0.6 * lower + 0.4 * higher);
    }
    return Math.round(combined * 0.5);
  }
  return (side[0]?.hcp_adj ?? side[0]?.handicap) || 0;
}
function mpPlayerAllowance(player, matchType) {
  const raw = (player?.hcp_adj ?? player?.handicap) || 0;
  return matchType === "fourball" ? Math.round(raw * 0.9) : raw;
}
function mpStrokesFor(allowance, strokeIndex, lowBaseline) {
  const diff = Math.max(0, allowance - lowBaseline);
  const base = Math.floor(diff / 18);
  const extra = diff % 18;
  return base + (strokeIndex <= extra ? 1 : 0);
}


// Texas Scramble team allowance: 25% of lowest HCP, 20% of 2nd lowest, 15% of 3rd, 10% of highest

// CONGU/WHS scramble allowance table varies by team size — was hardcoded
// for 4 players only. Now adapts to the group's actual member count, so an
// odd-sized group (e.g. a 3-ball among mostly 4-balls) still gets the
// correct table automatically regardless of which scramble format was
// selected at event setup.
function getScramblePcts(count) {
  if (count === 3) return [0.30, 0.20, 0.10];
  if (count === 2) return [0.35, 0.15];
  return [0.25, 0.20, 0.15, 0.10];
}
const calcScrambleAllowance = (members) => {
  const pcts = getScramblePcts(members.length);
  const sorted = [...members].sort((a, b) => ((a.hcp_adj ?? a.handicap) || 0) - ((b.hcp_adj ?? b.handicap) || 0));
  return Math.round(sorted.reduce((sum, p, i) => sum + ((p.hcp_adj ?? p.handicap) || 0) * (pcts[i] || 0), 0));
};

export default function Leaderboard() {
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTeamMode, setIsTeamMode] = useState(false);
  const [isMatchPlay, setIsMatchPlay] = useState(false);
  const [matches, setMatches] = useState([]);
  const [matchMeta, setMatchMeta] = useState({ isInterClub: false });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      const ev = eventRes.data;
      setEvent(ev);

      const teamMode = TEAM_FORMATS.includes(ev.game_type);
      setIsTeamMode(teamMode);
      const matchPlayMode = MATCH_PLAY_FORMATS.includes(ev.game_type);
      setIsMatchPlay(matchPlayMode);

      const isMedal = ev.game_type?.includes("Medal") || ev.game_type?.includes("Stroke");
      const isScramble = ev.game_type?.startsWith("Texas Scramble");

      const [scoresRes, holesRes] = await Promise.all([
        api.get("/scores/" + ev.id),
        api.get("/courses/" + ev.course_id + "/holes")
      ]);
      const holes = holesRes.data;
      const scores = scoresRes.data;

      if (matchPlayMode) {
        const isInterClub = TEAM_MATCH_PLAY_FORMATS.includes(ev.game_type);
        const [groupRes, teamsRes] = await Promise.all([
          api.get("/groups"),
          api.get("/teams").catch(() => ({ data: { teams: [] } }))
        ]);
        const players = (groupRes.data.players || []).filter(p => p.playing);

        const teamNameMap = {};
        (teamsRes.data.teams || []).forEach(t => { teamNameMap[t.id] = t.team_name; });
        const clubName = (teamId) => teamNameMap[teamId] || ('Team ' + teamId);

        const grpMap = {};
        players.forEach(p => {
          if (!grpMap[p.grp]) grpMap[p.grp] = {};
          const tid = p.team_id || 0;
          if (!grpMap[p.grp][tid]) grpMap[p.grp][tid] = [];
          grpMap[p.grp][tid].push(p);
        });

        const matchResults = [];
        Object.entries(grpMap).sort((a,b) => parseInt(a[0])-parseInt(b[0])).forEach(([grp, teamGroups]) => {
          const teamIds = Object.keys(teamGroups);
          if (teamIds.length < 2) return;
          const sideATeamId = parseInt(teamIds[0]);
          const sideBTeamId = parseInt(teamIds[1]);
          const sideA = teamGroups[teamIds[0]];
          const sideB = teamGroups[teamIds[1]];
          const nameA = sideA.map(p => p.name.split(" ")[0]).join("/");
          const nameB = sideB.map(p => p.name.split(" ")[0]).join("/");
          const clubA = clubName(sideATeamId);
          const clubB = clubName(sideBTeamId);
          const matchType = getMatchType(ev.game_type);
          const scoresA = scores.filter(s => sideA.some(p => p.player_id === s.player_id) && s.gross);
          const scoresB = scores.filter(s => sideB.some(p => p.player_id === s.player_id) && s.gross);
          const lowBaseline = matchType === "alternate"
            ? Math.min(mpSideAllowance(sideA, matchType, ev.game_type), mpSideAllowance(sideB, matchType, ev.game_type))
            : matchType === "fourball"
              ? Math.min(...[...sideA, ...sideB].map(p => mpPlayerAllowance(p, matchType)))
              : Math.min(mpPlayerAllowance(sideA[0], matchType), mpPlayerAllowance(sideB[0], matchType));
          let scoreAUp = 0, scoreBUp = 0;
          let closedAt = null;
          holes.forEach((hole, i) => {
            if (closedAt !== null) return;
            const a = scoresA.find(s => s.hole === hole.hole);
            const b = scoresB.find(s => s.hole === hole.hole);
            if (!a || !b) return;
            const allowA = matchType === "alternate" ? mpSideAllowance(sideA, matchType, ev.game_type)
              : mpPlayerAllowance(sideA.find(p => p.player_id === a.player_id), matchType);
            const allowB = matchType === "alternate" ? mpSideAllowance(sideB, matchType, ev.game_type)
              : mpPlayerAllowance(sideB.find(p => p.player_id === b.player_id), matchType);
            const netA = parseInt(a.gross) - mpStrokesFor(allowA, hole.stroke_index, lowBaseline);
            const netB = parseInt(b.gross) - mpStrokesFor(allowB, hole.stroke_index, lowBaseline);
            if (netA < netB) scoreAUp++;
            else if (netB < netA) scoreBUp++;
            const lead = Math.abs(scoreAUp - scoreBUp);
            const holesLeft = holes.length - (i + 1);
            if (lead > holesLeft) closedAt = i + 1;
          });
          const lead = Math.abs(scoreAUp - scoreBUp);
          const leaderName = scoreAUp > scoreBUp ? nameA : scoreBUp > scoreAUp ? nameB : null;
          const winningSide = scoreAUp > scoreBUp ? 'A' : scoreBUp > scoreAUp ? 'B' : null;
          const leaderClub = winningSide === 'A' ? clubA : winningSide === 'B' ? clubB : null;
          const thru = Math.min(scoresA.length, scoresB.length);
          const allHolesScored = scoresA.length === holes.length && scoresB.length === holes.length;
          const isComplete = closedAt !== null || allHolesScored;

          let margin = "";
          if (lead === 0 && isComplete) margin = "Halved";
          else if (lead === 0) margin = "All Square";
          else if (closedAt) {
            const remaining = holes.length - closedAt;
            margin = `${lead}&${remaining}`;
          } else if (isComplete) {
            margin = `${lead}UP`;
          } else {
            margin = `${lead}UP`;
          }

          let result = "No scores yet";
          let clubResult = "No scores yet";
          if (scoresA.length > 0 && scoresB.length > 0) {
            if (margin === "Halved" || margin === "All Square") {
              result = margin + (isComplete ? "" : (" thru " + thru));
              clubResult = result;
            } else if (isComplete) {
              result = `${leaderName} wins ${margin}`;
              clubResult = `Winner: ${leaderClub} ${margin}`;
            } else {
              result = `${leaderName} ${margin} thru ${thru}`;
              clubResult = `${leaderClub} ${margin} thru ${thru}`;
            }
          }

          matchResults.push({ grp: parseInt(grp), nameA, nameB, clubA, clubB, result, clubResult, thru, closed: isComplete, winningSide, isComplete });
        });
        setMatches(matchResults);
        setMatchMeta({ isInterClub });
        setLoading(false);
        return;
      }
      if (teamMode) {
        const teamsRes = await api.get("/teams");
        const players = (teamsRes.data.players || []).filter(p => p.playing);

        const teamResults = [];
        const isBest2of4 = GROUP_BEST_2_OF_4_FORMATS.includes(ev.game_type);

        if (isBest2of4) {
          // Flat groups of players (Groups.jsx grp, no pairing) — best 2
          // individual net/Stableford scores per hole count. Mirrors
          // results/GroupBestTwoResults.jsx's math exactly, kept in sync
          // with it deliberately rather than importing across pages.
          const grpMap = {};
          players.forEach(p => {
            if (!grpMap[p.grp]) grpMap[p.grp] = [];
            grpMap[p.grp].push(p);
          });

          Object.entries(grpMap).sort((a, b) => parseInt(a[0]) - parseInt(b[0])).forEach(([grp, members]) => {
            let total = 0, thru = 0;
            holes.forEach(hole => {
              const holeScores = members.map(p => {
                const s = scores.find(s => s.player_id === p.player_id && s.hole === hole.hole);
                if (!s || !parseInt(s.gross)) return null;
                const hcp = (p.hcp_adj ?? p.handicap) || 0;
                const base = Math.floor(hcp / 18);
                const extra = hcp % 18;
                const shots = base + (hole.stroke_index <= extra ? 1 : 0);
                const net = parseInt(s.gross) - shots;
                return isMedal ? net : Math.max(0, 2 + (hole.par - net));
              }).filter(s => s !== null).sort((a, b) => isMedal ? a - b : b - a);
              const best2 = holeScores.slice(0, 2);
              if (best2.length > 0) {
                total += best2.reduce((s, v) => s + v, 0);
                thru++;
              }
            });
            teamResults.push({ teamId: "grp" + grp, name: "Group " + grp, members: members.map(p => p.name.split(" ")[0]).join(", "), total: thru > 0 ? total : null, thru });
          });
        } else if (isScramble) {
          // Texas Scramble: each physical playing group is its own scramble team,
          // not the 2-named-team system used for Team Stableford/Medal below.
          const grpMap = {};
          players.forEach(p => {
            if (!grpMap[p.grp]) grpMap[p.grp] = [];
            grpMap[p.grp].push(p);
          });

          Object.entries(grpMap).sort((a, b) => parseInt(a[0]) - parseInt(b[0])).forEach(([grp, members]) => {
            const allowance = calcScrambleAllowance(members);
            const leadPlayer = members[0];
            const teamScores = scores.filter(s => s.player_id === leadPlayer.player_id);
            let total = 0, thru = 0;
            holes.forEach(hole => {
              const s = teamScores.find(s => s.hole === hole.hole);
              if (s) {
                const base = Math.floor(allowance / 18);
                const extra = allowance % 18;
                const shots = base + (hole.stroke_index <= extra ? 1 : 0);
                const net = parseInt(s.gross) - shots;
                // Texas Scramble traditionally scores by net strokes, not
                // points -- "Texas Scramble Medal" formats (added
                // 2026-09-09) score that way; the original points-based
                // "Texas Scramble" formats are unchanged.
                total += isMedal ? net : Math.max(0, 2 + (hole.par - net));
                thru++;
              }
            });
            teamResults.push({ teamId: "grp" + grp, name: "Group " + grp, members: members.map(p => p.name.split(" ")[0]).join(", "), total: thru > 0 ? total : null, thru });
          });
        } else if (ALTERNATE_SHOT_FORMATS.includes(ev.game_type)) {
          // Foursomes/Greensomes have no Team A/Team B at all — Auto Group
          // assigns each pair its own auto-generated team_id (see groups.js
          // generatePairGroups), never registered in event_teams. The
          // generic branch below builds teamMap FROM event_teams, so for
          // these formats it was always empty and the leaderboard showed
          // zero rows, not just wrong numbers (found 2026-09-04). Group
          // players by their own team_id directly instead, and use the
          // same 50%-combined / 60-40-split shared-ball formula already
          // correct in PairResults.jsx, rather than the per-player
          // best-of-members logic used for genuine multi-player teams.
          const isGreensomes = ev.game_type?.startsWith("Greensomes");
          const pairMap = {};
          players.forEach(p => {
            if (!p.team_id) return;
            if (!pairMap[p.team_id]) pairMap[p.team_id] = [];
            pairMap[p.team_id].push(p);
          });
          Object.entries(pairMap).forEach(([teamId, members]) => {
            if (members.length === 0) return;
            const combHcp = members.reduce((s, p) => s + ((p.hcp_adj ?? p.handicap) || 0), 0);
            const lowerHcp = Math.min(...members.map(p => (p.hcp_adj ?? p.handicap) || 0));
            const higherHcp = Math.max(...members.map(p => (p.hcp_adj ?? p.handicap) || 0));
            const allowance = isGreensomes ? Math.round(0.6 * lowerHcp + 0.4 * higherHcp) : Math.round(combHcp * 0.5);
            const leadPlayer = members[0];
            const pairScores = scores.filter(s => s.player_id === leadPlayer.player_id);
            let total = 0, thru = 0;
            holes.forEach(hole => {
              const s = pairScores.find(s => s.hole === hole.hole);
              if (s) {
                const base = Math.floor(allowance / 18);
                const extra = allowance % 18;
                const shots = base + (hole.stroke_index <= extra ? 1 : 0);
                const net = parseInt(s.gross) - shots;
                total += isMedal ? net : Math.max(0, 2 + (hole.par - net));
                thru++;
              }
            });
            teamResults.push({ teamId, name: members.map(p => p.name.split(" ")[0]).join(" / "), members: members.map(p => p.name.split(" ")[0]).join(", "), total: thru > 0 ? total : null, thru });
          });
        } else if (BETTER_BALL_HCP_FORMATS.includes(ev.game_type)) {
          // Better Ball / Fourball Better Ball pair via the Pairs page's own
          // team_id too, same as Foursomes/Greensomes above — never
          // registered in event_teams, so the generic named-teams branch
          // below always found zero members and showed nothing on the
          // leaderboard once a pair-format event actually had scores saved
          // (found live 2026-09-06, same root cause as the 2026-09-04
          // Foursomes/Greensomes fix, just never extended to this format
          // group). Unlike Foursomes/Greensomes' one shared ball, Better
          // Ball credits a different partner's own gross each hole — mirror
          // BestBallResults.jsx's per-player 85%-handicap best-of-pair math
          // exactly, not the alternate-shot combined-allowance formula.
          const pairMap = {};
          players.forEach(p => {
            if (!p.team_id) return;
            if (!pairMap[p.team_id]) pairMap[p.team_id] = [];
            pairMap[p.team_id].push(p);
          });
          Object.entries(pairMap).forEach(([teamId, members]) => {
            if (members.length === 0) return;
            let total = 0, thru = 0;
            holes.forEach(hole => {
              const holeScores = members.map(p => {
                const s = scores.find(s => s.player_id === p.player_id && s.hole === hole.hole);
                if (!s || !parseInt(s.gross)) return null;
                const rawHcp = (p.hcp_adj ?? p.handicap) || 0;
                const hcp = Math.round(rawHcp * 0.85);
                const base = Math.floor(hcp / 18);
                const extra = hcp % 18;
                const shots = base + (hole.stroke_index <= extra ? 1 : 0);
                const net = parseInt(s.gross) - shots;
                return isMedal ? net : Math.max(0, 2 + (hole.par - net));
              }).filter(s => s !== null).sort((a, b) => isMedal ? a - b : b - a);
              if (holeScores.length > 0) {
                total += holeScores[0];
                thru++;
              }
            });
            teamResults.push({ teamId, name: members.map(p => p.name.split(" ")[0]).join(" / "), members: members.map(p => p.name.split(" ")[0]).join(", "), total: thru > 0 ? total : null, thru });
          });
        } else {
          const teams = teamsRes.data.teams || [];
          const teamMap = {};
          teams.forEach(t => { teamMap[t.id] = { name: t.team_name, members: [] }; });
          players.forEach(p => { if (p.team_id && teamMap[p.team_id]) teamMap[p.team_id].members.push(p); });

          const isAggregate = ev.game_type?.startsWith("Team Aggregate");
          Object.entries(teamMap).forEach(([teamId, team]) => {
            const members = team.members;
            if (members.length === 0) return;
            let total = 0, thru = 0;
            holes.forEach(hole => {
              const holeScores = members.map(p => {
                const s = scores.find(s => s.player_id === p.player_id && s.hole === hole.hole);
                // A pair's non-counting partner is saved with gross=0 as a
                // placeholder (see BestBallResults.jsx/IndividualResults.jsx
                // for the same rule) — treating that as a real score of zero
                // makes it win every best-of-pair comparison, since 0 looks
                // like an impossibly good round. Found live on this page
                // 2026-09-02, same bug already fixed elsewhere.
                if (!s || !parseInt(s.gross)) return null;
                const rawHcp = (p.hcp_adj ?? p.handicap) || 0;
                let hcp;
                if (BETTER_BALL_HCP_FORMATS.includes(ev.game_type)) hcp = Math.round(rawHcp * 0.85);
                else { const hcpPct = HCP_95_FORMATS.includes(ev.game_type) ? 0.95 : 1; hcp = Math.round(rawHcp * hcpPct); }
                const base = Math.floor(hcp / 18);
                const extra = hcp % 18;
                const shots = base + (hole.stroke_index <= extra ? 1 : 0);
                const net = parseInt(s.gross) - shots;
                return isMedal ? net : Math.max(0, 2 + (hole.par - net));
              }).filter(s => s !== null).sort((a, b) => isMedal ? a - b : b - a);
              if (holeScores.length > 0) {
                total += isAggregate ? holeScores.reduce((s, v) => s + v, 0) : holeScores[0];
                thru++;
              }
            });
            teamResults.push({ teamId, name: team.name, members: members.map(p => p.name.split(" ")[0]).join(", "), total: thru > 0 ? total : null, thru });
          });
        }

        teamResults.sort((a, b) => {
          if (a.total === null && b.total === null) return 0;
          if (a.total === null) return 1;
          if (b.total === null) return -1;
          return isMedal ? a.total - b.total : b.total - a.total;
        });
        teamResults.forEach((p, i) => { p.pos = i + 1; });
        setResults(teamResults);

      } else {
        const playersRes = await api.get("/groups");
        const players = (playersRes.data.players || []).filter(p => p.playing);

        // Singles Match Play's own rule (confirmed 2026-09-04, this format
        // only): Playing HCP = player's own Course HCP minus the LOWEST
        // Course HCP in their own physical group (grp), not a percentage.
        const isSinglesMatchPlay = ev.game_type === "Singles Match Play";
        const playerResults = players.map(player => {
          const playerScores = scores.filter(s => s.player_id === player.player_id);
          let gross = 0, points = 0, net = 0;
          holes.forEach(hole => {
            const s = playerScores.find(s => s.hole === hole.hole);
            if (s) {
              let hcp;
              if (isSinglesMatchPlay) {
                const groupmates = players.filter(p => p.grp === player.grp);
                const lowest = Math.min(...groupmates.map(p => (p.hcp_adj ?? p.handicap) || 0));
                hcp = Math.round(((player.hcp_adj ?? player.handicap) || 0) - lowest);
              } else {
                const hcpPct = HCP_95_FORMATS.includes(ev.game_type) ? 0.95 : 1;
                hcp = Math.round(((player.hcp_adj ?? player.handicap) || 0) * hcpPct);
              }
              const base = Math.floor(hcp / 18);
              const extra = hcp % 18;
              const shots = base + (hole.stroke_index <= extra ? 1 : 0);
              const g = parseInt(s.gross);
              const n = g - shots;
              gross += g;
              net += n;
              points += Math.max(0, 2 + (hole.par - n));
            }
          });
          return { ...player, gross: gross || 0, net: net || 0, points: points || 0, thru: playerScores.length };
        });

        playerResults.sort((a, b) => {
          if (a.thru === 18 && b.thru !== 18) return -1;
          if (b.thru === 18 && a.thru !== 18) return 1;
          if (isMedal) return a.net - b.net;
          return b.points - a.points;
        });

        let pos = 1;
        playerResults.forEach((p, i) => {
          if (i === 0) { p.pos = 1; }
          else {
            const prev = playerResults[i - 1];
            const tied = isMedal ? p.net === prev.net : p.points === prev.points;
            p.pos = tied ? prev.pos : pos;
          }
          pos = i + 2;
        });

        setResults(playerResults);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const isMedal = event?.game_type?.includes("Medal") || event?.game_type?.includes("Stroke");
  const { isInterClub } = matchMeta;
  const matchAName = matches[0]?.clubA || 'Team A';
  const matchBName = matches[0]?.clubB || 'Team B';
  let teamAWins = 0, teamBWins = 0, halvedCount = 0;
  if (isInterClub) {
    matches.forEach(m => {
      if (!m.isComplete) return;
      if (m.winningSide === 'A') teamAWins++;
      else if (m.winningSide === 'B') teamBWins++;
      else halvedCount++;
    });
  }

  return (
    <Layout>
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ backgroundColor: "white", borderBottom: "1px solid #d1d5db", padding: "8px 16px", display: "flex", alignItems: "center", gap: "16px" }}>
          <span style={{ fontWeight: "600", fontSize: "14px" }}>{isMatchPlay ? "Match Scoreboard" : "Leaderboard"}</span>
          {event && <span style={{ fontSize: "12px", color: "#2563eb", fontWeight: "500" }}>{event.game_type}</span>}
          <a href="https://leaderboard.golfsocietymanager.com" target="_blank" rel="noreferrer"
            style={{ marginLeft: "auto", fontSize: "12px", color: "#16a34a", fontWeight: "500", textDecoration: "none" }}>
            Public Leaderboard ↗
          </a>
        </div>
        {isMatchPlay && isInterClub && matches.length > 0 && (
          <div style={{ backgroundColor: "#14532d", color: "white", padding: "12px 16px", textAlign: "center", fontWeight: "700", fontSize: "18px" }}>
            {matchAName.toUpperCase()} {teamAWins} - {teamBWins} {matchBName.toUpperCase()}
            {halvedCount > 0 && <span style={{ fontSize: "13px", fontWeight: "400" }}>&nbsp;&nbsp;({halvedCount} halved)</span>}
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {isMatchPlay ? (
            <table style={{ width: "100%", fontSize: "14px", borderCollapse: "collapse" }}>
              <thead style={{ position: "sticky", top: 0, backgroundColor: "#14532d", color: "white" }}>
                <tr>
                  <th style={{ padding: "8px 16px", textAlign: "center", width: "64px" }}>Group</th>
                  <th style={{ padding: "8px 16px", textAlign: "left" }}>Match</th>
                  <th style={{ padding: "8px 16px", textAlign: "center", width: "64px" }}>Thru</th>
                  <th style={{ padding: "8px 16px", textAlign: "left" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((m, i) => (
                  <tr key={m.grp} style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: i % 2 === 0 ? "white" : "#f0fdf4" }}>
                    <td style={{ padding: "8px 16px", textAlign: "center", fontWeight: "700" }}>{m.grp}</td>
                    <td style={{ padding: "8px 16px" }}>
                      {isInterClub ? `${m.nameA} (${m.clubA}) vs ${m.nameB} (${m.clubB})` : `${m.nameA} vs ${m.nameB}`}
                    </td>
                    <td style={{ padding: "8px 16px", textAlign: "center" }}>{m.closed ? "F" : (m.thru || "-")}</td>
                    <td style={{ padding: "8px 16px", fontWeight: "700", color: "#2563eb" }}>{isInterClub ? m.clubResult : m.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : isTeamMode ? (
            <table style={{ width: "100%", fontSize: "14px", borderCollapse: "collapse" }}>
              <thead style={{ position: "sticky", top: 0, backgroundColor: "#14532d", color: "white" }}>
                <tr>
                  <th style={{ padding: "8px 16px", textAlign: "center", width: "48px" }}>Pos</th>
                  <th style={{ padding: "8px 16px", textAlign: "left" }}>Team</th>
                  <th style={{ padding: "8px 16px", textAlign: "left" }}>Players</th>
                  <th style={{ padding: "8px 16px", textAlign: "center", width: "64px" }}>Thru</th>
                  <th style={{ padding: "8px 16px", textAlign: "center", width: "80px" }}>{isMedal ? "Net" : "Pts"}</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={r.teamId} style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: i % 2 === 0 ? "white" : "#f0fdf4" }}>
                    <td style={{ padding: "8px 16px", textAlign: "center", fontWeight: "700" }}>{r.pos}</td>
                    <td style={{ padding: "8px 16px", fontWeight: "700", color: "#14532d" }}>{r.name}</td>
                    <td style={{ padding: "8px 16px", fontSize: "12px", color: "#6b7280" }}>{r.members}</td>
                    <td style={{ padding: "8px 16px", textAlign: "center" }}>{r.thru === 18 ? "F" : r.thru || "-"}</td>
                    <td style={{ padding: "8px 16px", textAlign: "center", fontWeight: "700", color: "#2563eb" }}>{r.total !== null ? r.total : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table style={{ width: "100%", fontSize: "14px", borderCollapse: "collapse" }}>
              <thead style={{ position: "sticky", top: 0, backgroundColor: "#14532d", color: "white" }}>
                <tr>
                  <th style={{ padding: "8px 16px", textAlign: "center", width: "48px" }}>Pos</th>
                  <th style={{ padding: "8px 16px", textAlign: "left" }}>Player</th>
                  <th style={{ padding: "8px 16px", textAlign: "center", width: "64px" }} title="Course Handicap, adjusted for tee and capped per event settings">Course HCP</th>
                  <th style={{ padding: "8px 16px", textAlign: "center", width: "64px" }}>Thru</th>
                  <th style={{ padding: "8px 16px", textAlign: "center", width: "64px" }}>Gross</th>
                  <th style={{ padding: "8px 16px", textAlign: "center", width: "64px" }}>Net</th>
                  {!isMedal && <th style={{ padding: "8px 16px", textAlign: "center", width: "64px" }}>Pts</th>}
                </tr>
              </thead>
              <tbody>
                {results.map((p, i) => (
                  <tr key={p.player_id} style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: i % 2 === 0 ? "white" : "#f0fdf4" }}>
                    <td style={{ padding: "8px 16px", textAlign: "center", fontWeight: "700" }}>{p.thru === 18 ? p.pos : "-"}</td>
                    <td style={{ padding: "8px 16px", fontWeight: "500" }}>{p.name}</td>
                    <td style={{ padding: "8px 16px", textAlign: "center" }}>{p.hcp_adj ?? p.handicap}</td>
                    <td style={{ padding: "8px 16px", textAlign: "center" }}>{p.thru === 18 ? "F" : p.thru || "-"}</td>
                    <td style={{ padding: "8px 16px", textAlign: "center" }}>{p.gross || "-"}</td>
                    <td style={{ padding: "8px 16px", textAlign: "center" }}>{p.net || "-"}</td>
                    {!isMedal && <td style={{ padding: "8px 16px", textAlign: "center", fontWeight: "700", color: "#2563eb" }}>{p.points || "-"}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && results.length === 0 && matches.length === 0 && <div style={{ textAlign: "center", padding: "32px", color: "#9ca3af" }}>No scores entered yet.</div>}
        </div>
      </div>
    </Layout>
  );
}
